import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import MapView from "../components/MapView";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, getUser, isAdminUser } from "../lib/api";
import { currentPosition } from "../lib/native";
import { reverseGeocode } from "../lib/geocode";
import { MapPin, Search, Map as MapIcon, List as ListIcon, Compass, Recycle, Plus } from "lucide-react";

const CATEGORIES = ["All", "Furniture", "Electronics", "Toys", "Clothes", "Garden", "Books", "Other"];

function distanceKm(a, b) {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a[0] * Math.PI) / 180) *
      Math.cos((b[0] * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function ReuseBrowse() {
  const navigate = useNavigate();
  const user = getUser();
  const [listings, setListings] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [view, setView] = useState("split");
  const [nearby, setNearby] = useState(false);
  const [center, setCenter] = useState(null);
  const [areaLabel, setAreaLabel] = useState("");

  useEffect(() => {
    const params = { status: "available" };
    if (category !== "All") params.category = category;
    if (query.trim()) params.q = query.trim();
    api.get("/reuse-listings", { params }).then((r) => setListings(r.data)).catch(() => setListings([]));
  }, [category, query]);

  const enableNearby = () => {
    currentPosition().then(
      async ({ lat, lng }) => {
        setCenter([lat, lng]);
        setNearby(true);
        const label = await reverseGeocode(lat, lng);
        setAreaLabel(label);
      },
      () => {
        setCenter([54.5, -2.5]);
        setNearby(true);
        setAreaLabel("United Kingdom");
      }
    );
  };

  const filtered = useMemo(() => {
    if (!nearby || !center) return listings;
    return listings
      .map((l) => ({ ...l, distance_km: distanceKm(center, [l.lat, l.lng]) }))
      .filter((l) => l.distance_km <= 50)
      .sort((a, b) => a.distance_km - b.distance_km);
  }, [listings, nearby, center]);

  const markers = filtered.map((l) => ({
    id: l.id,
    lat: l.lat,
    lng: l.lng,
    title: l.title,
    subtitle: `${l.category} · ${l.postcode_area}`,
    is_demo: l.is_demo,
  }));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
          <div>
            <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px] tracking-wide">
              <Recycle className="w-3 h-3 mr-1 inline" /> Free stuff marketplace
            </Badge>
            <h1
              data-testid="reuse-browse-title"
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium"
              style={{ fontFamily: "Outfit" }}
            >
              Give &amp; take, free
            </h1>
            <p className="mt-2 text-sm text-[#6B6965] max-w-xl">
              Still usable? List it for free — a neighbour collects it and keeps it out of landfill.
              Exact addresses stay private until both sides agree.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Button
              data-testid="reuse-new-cta"
              onClick={() => navigate(user ? "/reuse/new" : "/login")}
              className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white"
            >
              <Plus className="w-4 h-4 mr-1" /> List an item
            </Button>
          </div>
        </div>

        {/* Filter row */}
        <div className="mt-6 flex flex-wrap gap-2 items-center">
          <div data-testid="reuse-cat-filter" className="flex flex-wrap gap-1.5 p-1 bg-[#EBE8E0] rounded-full">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`reuse-cat-${c.toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  category === c ? "bg-white shadow-sm text-[#1B3624]" : "text-[#6B6965] hover:text-[#1B3624]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6965]" />
            <Input
              data-testid="reuse-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title or description…"
              className="pl-10 rounded-full bg-white"
            />
          </div>
          {isAdminUser(user) && (
            <div className="hidden md:flex p-1 bg-[#EBE8E0] rounded-full">
              {[
                { id: "split", icon: <MapIcon className="w-4 h-4" /> },
                { id: "list", icon: <ListIcon className="w-4 h-4" /> },
              ].map((v) => (
                <button
                  key={v.id}
                  data-testid={`reuse-view-${v.id}`}
                  onClick={() => setView(v.id)}
                  className={`p-2 rounded-full ${view === v.id ? "bg-white shadow-sm" : ""}`}
                >
                  {v.icon}
                </button>
              ))}
            </div>
          )}
          <Button
            data-testid="reuse-nearby-toggle"
            variant={nearby ? "default" : "outline"}
            onClick={() => (nearby ? setNearby(false) : enableNearby())}
            className={`rounded-full ${nearby ? "bg-[#1B3624] hover:bg-[#274d33] text-white" : "border-[#1B3624] text-[#1B3624]"}`}
          >
            <Compass className="w-4 h-4 mr-1" />
            {nearby ? "Near me · on" : "Near me"}
          </Button>
        </div>
        {nearby && areaLabel && (
          <div className="mt-2 text-xs text-[#6B6965]">Searching within 50 km of <span className="text-[#1B3624] font-medium">{areaLabel}</span></div>
        )}

        <div className={`mt-8 grid gap-6 ${view === "split" && isAdminUser(user) ? "lg:grid-cols-2" : ""}`}>
          {view !== "list" && isAdminUser(user) && (
            <div className="fade-up delay-1">
              <MapView
                markers={markers}
                center={center || [54.5, -2.5]}
                zoom={center ? 12 : 5}
                height="h-[500px]"
                testId="reuse-map"
              />
              <p className="mt-2 text-[11px] text-[#9B9893]">
                Pin locations are approximate (±150 m) — exact addresses stay private until a request is accepted.
              </p>
            </div>
          )}
          <div className={`space-y-3 ${view === "split" ? "lg:max-h-[500px] lg:overflow-y-auto lg:pr-2" : ""}`}>
            {filtered.length === 0 && (
              <Card className="p-10 text-center rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-[#6B6965]" data-testid="reuse-empty">
                No listings match your filter yet.
              </Card>
            )}
            {filtered.map((l) => (
              <Card
                key={l.id}
                role="button"
                tabIndex={0}
                data-testid={`reuse-card-${l.id}`}
                onClick={() => navigate(`/reuse/${l.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/reuse/${l.id}`); } }}
                className="p-5 rounded-2xl border-[#E2DFD8] bg-white hover:border-[#4A6B56] transition-colors flex gap-4 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4A6B56]"
              >
                {l.image_urls?.[0] && (
                  <img src={l.image_urls[0]} alt="" className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px]">{l.category}</Badge>
                    <Badge className="bg-[#4A6B56] text-white hover:bg-[#4A6B56] uppercase text-[10px]">Free</Badge>
                  </div>
                  <div className="mt-1.5 font-medium truncate" style={{ fontFamily: "Outfit" }}>{l.title}</div>
                  <div className="text-xs text-[#6B6965] mt-1 flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3 shrink-0" /> {l.postcode_area}{l.distance_km != null ? ` · ${l.distance_km.toFixed(1)} km` : ""}
                  </div>
                  <div className="text-xs text-[#6B6965] mt-1 truncate">Listed by {l.owner_name}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
